import Constants from '../../Constants';
import ImageRegistry from '../../ImageRegistry';
import VirtualDomElement from '../VirtualDomElement';
import ImageElement from './ImageElement';
import MultilineTextElement from './MultilineTextElement';
import RectangleElement from './RectangleElement';

export default class DocumentCard extends VirtualDomElement {
  borderBottom;
  urls: string[];
  maxImageWidth: number;
  maxImageHeight: number;

  textElement: MultilineTextElement;
  panelElement: RectangleElement;
  imageElements: ImageElement[];
  private imageRegistry: ImageRegistry;
  private documentId: any;
  private minHeight: number;

  constructor({
    documentId = null,
    x = 0,
    y = 0,
    maxImageWidth = 200,
    maxImageHeight = 200,
    text = '',
    fontSize = 10,
    urls = [],
    borderBottom = 0,
    drawBorder = false,
    borderColor = '#ccc',
    imageRegistry = null,
    onRequestDraw = () => null,
  } = {}) {
    super(onRequestDraw);

    this.documentId = documentId;
    this.borderBottom = borderBottom;
    this.urls = urls;
    this.maxImageWidth = maxImageWidth;
    this.maxImageHeight = maxImageHeight;
    this.imageRegistry = imageRegistry;
    this.minHeight = maxImageHeight / 2 + borderBottom;

    this.set('x', x);
    this.set('y', y);
    this.set('borderColor', borderColor);
    this.set('scale', 1);
    this.set('drawBorder', drawBorder);

    this.set('width', maxImageWidth);
    this.set('height', maxImageHeight * 2 + this.borderBottom);

    this.textElement = new MultilineTextElement({
      text: text,
      fontSize: fontSize,
      x: x + maxImageWidth * 0.05,
      y: y + maxImageHeight * 1.5 + this.borderBottom / 2 + fontSize,
      maxCharacterWidth: 24,
      height: 10,
      maxLines: 3,
      ellipsis: true,
      textAlign: 'left',
    });

    this.panelElement = new RectangleElement(
      x,
      y,
      maxImageWidth,
      maxImageHeight * 1.5 + this.borderBottom * 1.5,
    );
    this.imageElements = [];
    this.initializeImages();
  }

  getDocumentId() {
    return this.documentId;
  }

  getPanelElement() {
    return this.panelElement;
  }

  getTextElement() {
    return this.textElement;
  }

  initializeImages() {
    for (let i = 0; i <= 2; i++) {
      if (i < this.urls.length) {
        const url = this.urls[i];
        let x = this.get('x');
        let y = this.get('y');
        let width = this.maxImageWidth;
        if (i > 0) {
          width /= 2;

          x += ((i - 1) * this.maxImageWidth) / 2;
          y += this.maxImageHeight / 2;
        }

        const imageElement = new ImageElement({
          x: x,
          y: y,
          url: url,
          maxWidth: width,
          minRatio: Constants.documentPoint.minRatioDocumentPicture,
          maxRatio: Constants.documentPoint.maxRatioDocumentPicture,
          imageRegistry: this.imageRegistry,
          onRequestDraw: this.onRequestDraw,
        });
        this.imageElements.push(imageElement);
      }
    }
  }

  getImageElements() {
    return this.imageElements;
  }

  // triggered when one of the images is loaded
  onLoad(image, src) {
    let allChildrenLoaded = true;
    for (const imageElement of this.getImageElements()) {
      if (imageElement.get('url') === src) {
        imageElement.onLoad(image);
      }
      allChildrenLoaded = allChildrenLoaded && imageElement.get('loaded');
    }

    if (allChildrenLoaded) {
      // if all images loaded -> create layout
      this.updateLayout();
      this.set('layoutCalculated', true);
    }

    this.requestDraw();
  }

  onError() {
    //url could not be loaded
  }

  updateLayout() {
    const offsetY = this.get('y');
    const offsetX = this.get('x');

    const pictureElements = this.getImageElements();
    let heightTopElement = 0;
    if (pictureElements.length > 0) {
      const ratioTopElement =
        pictureElements[0].get('naturalHeight') /
        pictureElements[0].get('naturalWidth');
      heightTopElement = pictureElements[0].get('width') * ratioTopElement;
      pictureElements[0].set('height', heightTopElement);
    }

    let heightBottomLeftElement = 0;
    if (pictureElements.length > 1) {
      pictureElements[1].set('y', offsetY + heightTopElement);
      const ratioBottomLeftElement =
        pictureElements[1].get('naturalHeight') /
        pictureElements[1].get('naturalWidth');
      heightBottomLeftElement =
        pictureElements[1].get('width') * ratioBottomLeftElement;
      pictureElements[1].set('height', heightBottomLeftElement);
    }

    let heightBottomRightElement = 0;
    if (pictureElements.length > 2) {
      pictureElements[2].set('y', offsetY + heightTopElement);
      const ratioBottomRight =
        pictureElements[2].get('naturalHeight') /
        pictureElements[2].get('naturalWidth');
      heightBottomRightElement =
        pictureElements[2].get('width') * ratioBottomRight;
      pictureElements[2].set('height', heightBottomRightElement);
    }

    const heightImageBlock = Math.max(
      this.minHeight,
      heightTopElement +
        Math.max(heightBottomLeftElement, heightBottomRightElement),
    );
    const panelHeight = heightImageBlock + this.borderBottom;

    this.panelElement.set('x', offsetX);
    this.panelElement.set('y', offsetY);
    this.panelElement.set('width', this.maxImageWidth);
    this.panelElement.set('height', panelHeight);

    this.textElement.set(
      'y',
      this.get('y') + heightImageBlock + (1 / 3) * this.borderBottom,
    );
    this.set('height', panelHeight);
  }

  draw(ctx, transform, scaling, drawTitle = true) {
    let [x, y] = [this.get('x'), this.get('y')];
    [x, y] = transform.apply([x, y]);
    [x, y] = this.getFlooredCoordinates([x, y]);

    const imagePanel = this.getPanelElement();

    const widthPanel = imagePanel.get('width');
    const heightPanel = imagePanel.get('height');
    ctx.translate(
      x + (transform.k * widthPanel) / 2,
      y + (transform.k * heightPanel) / 2,
    );
    ctx.scale(this.get('scale'), this.getAttribute('scale'));
    ctx.translate(
      -x - (transform.k * widthPanel) / 2,
      -y - (transform.k * heightPanel) / 2,
    );
    ctx.fillStyle = 'rgb(255,255,255)';
    ctx.shadowBlur =
      scaling.getScaleFactor() *
      Constants.documentPoint.shadowBlur *
      transform.k;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.shadowColor = 'rgba(142,124,124,0.76)'; // use topic color

    // draw background panel
    ctx.fillRect(x, y, widthPanel * transform.k, heightPanel * transform.k);
    ctx.shadowBlur = 0;

    const imageElements = this.getImageElements();
    for (const imageElement of imageElements) {
      imageElement.draw(ctx, transform);
    }

    // draw title
    if (drawTitle) {
      this.getTextElement().draw(ctx, transform);
    }

    if (this.get('drawBorder')) {
      ctx.strokeStyle = this.get('borderColor');
      ctx.rect(x, y, widthPanel * transform.k, heightPanel * transform.k);
      ctx.stroke();
      ctx.beginPath();
    }
  }

  override drawBoundingBox(ctx, transform) {
    super.drawBoundingBox(ctx, transform);
    this.panelElement.drawBoundingBox(ctx, transform);
    this.getTextElement().drawBoundingBox(ctx, transform);
  }
}
